import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogActions, DialogContent, DialogTitle, Snackbar,
    DialogContentText, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';

// --- INTERFACES ---
interface User {
    id: number;
    username: string;
    role: 'admin' | 'user';
    created_at: string;
}

// --- COMPONENTE PRINCIPAL ---
const UsersPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<Partial<User> & { password?: string } | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean, user: User | null }>({ open: false, user: null });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/users`); 
            setUsers(res.data);
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al cargar los usuarios.' });
        } finally {
            setIsLoading(false);
        }
    }, []);

    // --- CORRECCIÓN: Se activa la llamada a fetchData al cargar la página ---
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenForm = (user: Partial<User> | null = null) => {
        setEditingUser(user || { username: '', role: 'user', password: '' });
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingUser(null);
    };

    const handleSave = async (formData: Partial<User> & { password?: string }) => {
        const method = formData.id ? 'put' : 'post';
        const url = formData.id ? `${API_BASE_URL}/users/${formData.id}` : `${API_BASE_URL}/users`;

        try {
            await axios({ method, url, data: formData });
            setNotification({ type: 'success', message: `Usuario "${formData.username}" guardado.` });
            handleCloseForm();
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al guardar el usuario.' });
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm.user) return;
        try {
            await axios.delete(`${API_BASE_URL}/users/${deleteConfirm.user.id}`);
            setNotification({ type: 'success', message: 'Usuario eliminado.' });
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al eliminar el usuario.' });
        } finally {
            setDeleteConfirm({ open: false, user: null });
        }
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={6000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                    <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                    Gestión de Usuarios
                </Typography>
                <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => handleOpenForm()}>
                    Crear Usuario
                </Button>
            </Stack>

            {isFormOpen && (
                <UserForm
                    open={isFormOpen}
                    onClose={handleCloseForm}
                    onSave={handleSave}
                    user={editingUser}
                />
            )}

            <UsersTable
                users={users}
                onEdit={handleOpenForm}
                onDelete={(user) => setDeleteConfirm({ open: true, user })}
            />

            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, user: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que quieres eliminar al usuario **"{deleteConfirm.user?.username}"**? Esta acción es irreversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, user: null })}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// --- COMPONENTE DEL FORMULARIO ---
const UserForm: React.FC<{
    open: boolean;
    onClose: () => void;
    onSave: (data: Partial<User> & { password?: string }) => void;
    user: Partial<User> & { password?: string } | null;
}> = ({ open, onClose, onSave, user }) => {
    const [formData, setFormData] = useState<Partial<User> & { password?: string }>({});

    useEffect(() => {
        if (user) setFormData(user);
    }, [user]);

    const handleChange = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{user?.id ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 2 }}>
                    <TextField
                        autoFocus
                        label="Nombre de Usuario"
                        value={formData.username || ''}
                        onChange={(e) => handleChange('username', e.target.value)}
                        fullWidth
                        disabled={!!user?.id}
                    />
                    <TextField
                        label={user?.id ? "Nueva Contraseña (opcional)" : "Contraseña"}
                        type="password"
                        value={formData.password || ''}
                        onChange={(e) => handleChange('password', e.target.value)}
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Rol</InputLabel>
                        <Select value={formData.role || 'user'} label="Rol" onChange={(e) => handleChange('role', e.target.value)}>
                            <MenuItem value="user">Usuario</MenuItem>
                            <MenuItem value="admin">Administrador</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={() => onSave(formData)} variant="contained">Guardar</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- COMPONENTE DE LA TABLA ---
const UsersTable: React.FC<{
    users: User[];
    onEdit: (user: User) => void;
    onDelete: (user: User) => void;
}> = ({ users, onEdit, onDelete }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.1) };
    
    return (
        <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>ID</TableCell>
                            <TableCell sx={headerCellStyle}>Nombre de Usuario</TableCell>
                            <TableCell sx={headerCellStyle}>Rol</TableCell>
                            <TableCell sx={headerCellStyle}>Fecha de Creación</TableCell>
                            <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id} hover>
                                <TableCell>{user.id}</TableCell>
                                <TableCell>{user.username}</TableCell>
                                <TableCell>
                                    <Chip label={user.role} color={user.role === 'admin' ? 'primary' : 'default'} size="small" />
                                </TableCell>
                                <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Editar"><IconButton onClick={() => onEdit(user)}><EditIcon /></IconButton></Tooltip>
                                    <Tooltip title="Eliminar"><IconButton onClick={() => onDelete(user)} color="error"><DeleteIcon /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default UsersPage;
